import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
  useTheme,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  PlusIcon,
  CaretLeftIcon,
  CaretRightIcon,
  ArrowLeftIcon,
} from '@phosphor-icons/react';
import { StageData } from '../../../utils/redux/slices/callStagesFlowSlice';

interface FlowSidebarProps {
  onAddStage: (stageData: Partial<StageData>) => void;
  hasUnsavedChanges: boolean;
  onNavigateBack: () => void;
}

const FlowSidebar: React.FC<FlowSidebarProps> = ({
  onAddStage,
  onNavigateBack,
}) => {
  const theme = useTheme();
  const [isExpanded, setIsExpanded] = useState(true);

  const addCustomStage = () => {
    onAddStage({
      stageName: 'Custom Stage',
      systemPrompt: 'Enter your custom system prompt here...',
      voice: 'default',
      temperature: 0.7,
    });
  };

  const handleGoBack = () => {
    onNavigateBack();
  };

  return (
    <Box
      sx={{
        width: isExpanded ? 320 : 64,
        backgroundColor: '#FFFFFF',
        borderRight: `1px solid rgba(0, 0, 0, 0.08)`,
        transition: 'width 0.3s ease',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          p: 2,
          borderBottom: `1px solid rgba(0, 0, 0, 0.08)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: isExpanded ? 'space-between' : 'center',
          flexDirection: isExpanded ? 'row' : 'column',
          gap: isExpanded ? 0 : 1,
        }}
      >
        {isExpanded ? (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              {/* Back Button */}
              <Tooltip title="Go Back">
                <IconButton
                  onClick={handleGoBack}
                  size="small"
                  sx={{
                    '&:hover': { backgroundColor: '#E8EBF1' },
                    transition: 'all 0.2s ease',
                    color: theme.palette.text.secondary,
                  }}
                >
                  <ArrowLeftIcon size={18} />
                </IconButton>
              </Tooltip>

              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: 18,
                  color: 'custom.textBlue',
                  letterSpacing: '-0.2px',
                }}
              >
                Conversation Flow
              </Typography>
            </Box>

            <Tooltip title="Collapse">
              <IconButton
                onClick={() => setIsExpanded(!isExpanded)}
                size="small"
                sx={{
                  '&:hover': { backgroundColor: '#E8EBF1' },
                  transition: 'all 0.2s ease',
                }}
              >
                <CaretLeftIcon />
              </IconButton>
            </Tooltip>
          </>
        ) : (
          <>
            {/* Back Button - Collapsed */}
            <Tooltip title="Go Back">
              <IconButton
                onClick={handleGoBack}
                size="small"
                sx={{
                  '&:hover': { backgroundColor: '#E8EBF1' },
                  transition: 'all 0.2s ease',
                  color: theme.palette.text.secondary,
                  width: 48,
                  height: 48,
                  borderRadius: '6px',
                }}
              >
                <ArrowLeftIcon size={18} />
              </IconButton>
            </Tooltip>

            {/* Expand Button - Collapsed */}
            <Tooltip title="Expand">
              <IconButton
                onClick={() => setIsExpanded(!isExpanded)}
                size="small"
                sx={{
                  '&:hover': { backgroundColor: '#E8EBF1' },
                  transition: 'all 0.2s ease',
                  width: 48,
                  height: 48,
                  borderRadius: '6px',
                }}
              >
                <CaretRightIcon />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>

      {/* Content */}
      <Box
        sx={{
          p: 2,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: isExpanded ? 'stretch' : 'center',
        }}
      >
        {isExpanded ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Custom Stage Button */}
            <Button
              variant="contained"
              startIcon={<PlusIcon size={16} />}
              onClick={addCustomStage}
              sx={{
                fontSize: 14,
                fontWeight: 500,
                borderRadius: '6px',
                textTransform: 'none',
                boxShadow: 'none',
                transition: 'all 0.3s ease',
                backgroundColor: theme.palette.primary.main,
                justifyContent: 'flex-start',
                py: 1.5,
                '&:hover': {
                  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                  backgroundColor: theme.palette.custom.tertiary,
                },
              }}
            >
              <Box sx={{ textAlign: 'left' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                  Add New Stage
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ opacity: 0.8, fontSize: 12 }}
                >
                  Create your own stage
                </Typography>
              </Box>
            </Button>

            <Divider sx={{ borderColor: 'rgba(0, 0, 0, 0.08)', my: 1 }} />
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            {/* Custom Stage Button - Collapsed */}
            <IconButton
              onClick={addCustomStage}
              sx={{
                backgroundColor: theme.palette.primary.main,
                color: 'white',
                width: 48,
                height: 48,
                borderRadius: '6px',
                '&:hover': {
                  backgroundColor: theme.palette.custom.tertiary,
                  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                },
              }}
            >
              <PlusIcon size={16} />
            </IconButton>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default FlowSidebar;
